/**
 * Retry an async operation with exponential backoff.
 *
 * Delays between attempts are baseDelayMs, baseDelayMs*2, baseDelayMs*4, ...
 * Throws the last error once attempts are exhausted or when shouldRetry
 * returns false for an error.
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: {
    attempts?: number;
    baseDelayMs?: number;
    shouldRetry?: (error: unknown) => boolean;
  } = {},
): Promise<T> {
  const { attempts = 3, baseDelayMs = 1000, shouldRetry = () => true } = options;
  let lastError: unknown;
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      return await fn();
    } catch (e) {
      lastError = e;
      if (attempt === attempts - 1 || !shouldRetry(e)) throw e;
      await new Promise((resolve) => setTimeout(resolve, baseDelayMs * 2 ** attempt));
    }
  }
  throw lastError;
}
